<template>
	<section>
		<div class="page__title page__title--with-buttons">
			<h1>Communauté</h1>
		</div>
		<p class="page__description">
			Découvrez les profils des enseignants de la communauté et leurs
			contributions.
		</p>

		<div class="all-filter-container" v-if="showSearch">
			<form class="filter-desc-title">
				<img
					class="magnifying-glass"
					src="/img/magnifying-glass.svg"
					alt=""
				/>
				<input
					class="search-input"
					v-model="searchKeyword"
					type="text"
					placeholder="Rechercher par mots clefs"
				/>
			</form>
			<div class="all-filter-wrapper">
				<div class="filter-wrapper my-content">
					<select
						name="subject"
						id="subject"
						v-model="selectedSubject"
						@change="onSubjectChange"
					>
						<option value="">Toutes les matières</option>
						<option
							v-for="subject in subjects"
							:key="subject.id"
							:value="subject.id"
						>
							{{ subject.name }}
						</option>
					</select>
				</div>
				<div class="filter-wrapper">
					<select
						name="level"
						id="level"
						v-model="selectedLevel"
						@change="onLevelChange"
					>
						<option value="">Tous les niveaux</option>
						<option
							v-for="level in levels"
							:key="level.id"
							:value="level.id"
						>
							{{ level.name }}
						</option>
					</select>
				</div>
				<div class="filter-wrapper">
					<select
						class="filter-chapter"
						name="chapter"
						id="chapter"
						v-model="selectedChapter"
					>
						<option value="">Tous les chapitres</option>
						<optgroup
							v-for="sequence in availableSequences"
							:key="sequence.id"
							:label="sequence.name"
						>
							<option
								v-for="chapter in $store.getters.chapters(
									sequence.id
								)"
								:key="chapter.id"
								:value="chapter.id"
							>
								{{ chapter.name }}
							</option>
						</optgroup>
					</select>
				</div>
			</div>
		</div>

		<div class="profiles__container">
			<div
				class="profile__card"
				v-for="profile in communityProfiles"
				:key="profile.id"
				@click="openProfile(profile.id)"
				role="button"
				tabindex="0"
			>
				<ProfilePicture
					:mediaPath="profile.profile_picture"
					:fullName="getFullName(profile)"
					size="full"
					:altText="`Photo de profil de ${getFullName(profile)}`"
					:editable="false"
					:profileId="profile.id"
				/>
				<div class="profile__details">
					<div class="profile__infos">
						<h3 class="profile__name">
							{{ getFullName(profile) }}
						</h3>
						<div class="profile__location">
							<img
								src="/img/profiles/location.svg"
								alt="Location"
							/>
							<span>{{ profile.city || "Non renseignée" }}</span>
						</div>
					</div>
					<div class="profile__section">
						<span class="profile__label">Niveaux</span>
						<div class="profile__levels">
							<span
								class="profile__level"
								v-for="(level, idx) in getDisplayedLevels(
									profile
								)"
								:key="level.id || idx"
							>
								{{ getLevel(level) }}
							</span>
							<span
								v-if="
									profile.levels && profile.levels.length > 2
								"
								class="profile__more"
								>+{{ profile.levels.length - 2 }}</span
							>
						</div>
					</div>
					<div class="profile__section">
						<span class="profile__label">Matières</span>
						<div class="profile__subjects">
							<span
								class="profile__subject"
								v-for="(subject, idx) in getDisplayedSubjects(
									profile
								)"
								:key="subject.id || idx"
							>
								{{ getSubject(subject)
								}}<span
									v-if="
										idx <
										getDisplayedSubjects(profile).length - 1
									"
									>,</span
								>
							</span>
							<span
								v-if="
									profile.subjects &&
									profile.subjects.length > 2
								"
								class="profile__more"
								>+{{ profile.subjects.length - 2 }}</span
							>
						</div>
					</div>
					<!-- <div class="profile__section">
						<span class="profile__label">Expérience</span>
						<span class="profile__experience">{{ formatExperience(profile.experience_years) }}</span>
					</div> -->
				</div>
			</div>

			<Loader v-if="loading" />
		</div>
	</section>
</template>

<script>
import { getAllCommunityProfilesService } from "@/services/communityProfileService";
import ProfilePicture from "./ProfilePicture.vue";
import Loader from "../../Loader.vue";

export default {
	name: "CommunityProfiles",
	components: {
		ProfilePicture,
		Loader,
	},

	data() {
		return {
			communityProfiles: [],
			communityProfilesRaw: null,
			loading: false,
			// filtres
			selectedSubject: "",
			selectedLevel: "",
			selectedChapter: "",
			searchKeyword: "",
			showSearch: true,
			searchTimeout: null,
		};
	},

	computed: {
		subjects() {
			return this.$store.state.subjects || [];
		},
		levels() {
			return this.$store.state.levels || [];
		},
		availableChapters() {
			if (!this.selectedSubject || !this.selectedLevel) {
				return [];
			}

			const sequences = this.$store.getters.sequencesByLevelAndSubject(
				this.selectedLevel,
				this.selectedSubject
			);
			if (!sequences || sequences.length === 0) {
				return [];
			}

			const chapters = [];
			sequences.forEach((sequence) => {
				const sequenceChapters = this.$store.getters.chapters(
					sequence.id
				);
				if (sequenceChapters && sequenceChapters.length > 0) {
					chapters.push(...sequenceChapters);
				}
			});

			return chapters;
		},
		availableSequences() {
			if (!this.selectedSubject || !this.selectedLevel) {
				return [];
			}
			return this.$store.getters.sequencesByLevelAndSubject(
				this.selectedLevel,
				this.selectedSubject
			);
		},
	},

	watch: {
		selectedSubject() {
			this.updateURL();
			this.emitFiltersChange();
		},
		selectedLevel() {
			this.updateURL();
			this.emitFiltersChange();
		},
		selectedChapter() {
			this.updateURL();
			this.emitFiltersChange();
		},
		searchKeyword() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				this.updateURL();
				this.emitFiltersChange();
			}, 500);
		},

		"$route.query": {
			handler(newQuery, oldQuery) {
				if (JSON.stringify(newQuery) !== JSON.stringify(oldQuery)) {
					this.loadFiltersFromURL();
				}
			},
			deep: true,
		},
	},

	methods: {
		async loadCommunityProfiles() {
			try {
				this.loading = true;
				const response = await getAllCommunityProfilesService();
				this.communityProfilesRaw = Array.isArray(response)
					? response
					: [];
				this.communityProfiles = this.communityProfilesRaw;
			} catch (error) {
				console.error(
					"Erreur lors du chargement des profils communautaires:",
					error
				);
				this.communityProfiles = [];
				this.communityProfilesRaw = [];
			} finally {
				this.loading = false;
			}
		},

		getFullName(profile) {
			if (!profile) return "";
			if (profile.full_name) {
				const nameParts = profile.full_name.split(" ");
				if (nameParts.length >= 2) {
					const firstName = nameParts[0];
					const lastName = nameParts[nameParts.length - 1];
					return `${firstName} ${lastName.charAt(0)}.`;
				}
				return profile.full_name;
			}
			// fallback
			return `${profile.first_name || ""} ${
				profile.last_name || ""
			}`.trim();
		},

		formatExperience(years) {
			if (!years) return "Non renseignée";
			if (years < 2) return "Moins de 2 ans";
			if (years <= 7) return "2 - 7 ans";
			return "7 ans et plus";
		},

		onSubjectChange() {
			this.selectedChapter = "";
			this.updateURL();
			this.emitFiltersChange();
		},
		onLevelChange() {
			this.selectedChapter = "";
			this.updateURL();
			this.emitFiltersChange();
		},

		emitFiltersChange() {
			const filters = {};
			if (this.selectedSubject) filters.subject = this.selectedSubject;
			if (this.selectedLevel) filters.level = this.selectedLevel;
			if (this.selectedChapter) filters.chapter = this.selectedChapter;
			if (this.searchKeyword) filters.search = this.searchKeyword;

			// pour l'instant on filtre localement la liste
			this.applyLocalFilters(filters);
		},

		updateURL() {
			const query = {};
			if (this.selectedSubject) query.subject = this.selectedSubject;
			if (this.selectedLevel) query.level = this.selectedLevel;
			if (this.selectedChapter) query.chapter = this.selectedChapter;
			if (this.searchKeyword) query.search = this.searchKeyword;

			if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
				this.$router.replace({ query });
			}
		},

		loadFiltersFromURL() {
			const query = this.$route.query;
			this.selectedSubject = query.subject || "";
			this.selectedLevel = query.level || "";
			this.selectedChapter = query.chapter || "";
			this.searchKeyword = query.search || "";
			this.emitFiltersChange();
		},

		applyLocalFilters(filters) {
			// Filtrage basique sur les champs disponibles dans communityProfiles
			let results = this.communityProfilesRaw || this.communityProfiles;
			if (filters.search) {
				const kw = filters.search.toLowerCase();
				results = results.filter(
					(p) =>
						(p.full_name || "").toLowerCase().includes(kw) ||
						(p.city || "").toLowerCase().includes(kw)
				);
			}
			if (filters.subject) {
				results = results.filter((p) =>
					(p.subjects || []).some(
						(s) => String(s.id) === String(filters.subject)
					)
				);
			}
			if (filters.level) {
				results = results.filter((p) =>
					(p.levels || []).some(
						(l) => String(l.id) === String(filters.level)
					)
				);
			}
			// chapter non disponible sur profil; ignore pour l'instant
			this.communityProfiles = results;
		},

		getLevel(level) {
			return level ? level.name : "Niveau inconnu";
		},

		getSubject(subject) {
			return subject ? subject.name : "Matière inconnue";
		},

		getDisplayedLevels(profile) {
			if (!profile || !profile.levels) return [];
			// profile.levels peut être un tableau de strings ou d'objets
			return profile.levels.slice(0, 2);
		},

		getDisplayedSubjects(profile) {
			if (!profile || !profile.subjects) return [];
			return profile.subjects.slice(0, 2);
		},

		openProfile(id) {
			if (!id) return;
			this.$router.push({
				name: "CommunityProfilePublic",
				params: { id },
			});
		},
	},

	async created() {
		await this.loadCommunityProfiles();
	},

	beforeUnmount() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
};
</script>

<style>
.profiles__container {
	margin-top: 2rem;
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 1rem 5%;
}

.profile__card {
	background: white;
	border-radius: 8px;
	box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.15);
	border: 1px solid #d6dded;
	cursor: pointer;
}

.profile__card .profile-picture {
	border: 0;
	border-radius: 7px 7px 0 0;
	max-height: 220px;
	box-shadow: none;
}

.profile__details {
	padding: 10px;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.profile__infos {
	display: flex;
	justify-content: space-between;
}

.profile__name {
	font-size: 14px;
}

.profile__location {
	gap: 8px;
	display: flex;
	align-items: center;
	font-size: 12px;
	font-weight: 600;
	color: #757575;
}

.profile__section {
	display: flex;
	flex-direction: row;
	gap: 16px;
	margin-top: 8px;
	align-items: center;
}

.profile__label {
	font-size: 14px;
	font-weight: 500;
	color: #757575;
	width: 60px;
}

.profile__levels,
.profile__subjects {
	display: flex;
	gap: 8px;
	width: 220px;
	flex-wrap: wrap;
}

.profile__level {
	font-size: 12px;
	font-weight: 500;
	padding: 2px 6px;
	background: rgba(0, 0, 0, 0.1);
	border-radius: 2px;
}

.profile__subject {
	font-size: 14px;
	color: #110eff;
}

.profile__experience,
.profile__more {
	font-size: 14px;
}
</style>
